<script setup>
const props = defineProps({
  title: {
    type: String,
    default: undefined,
  },
  icon: {
    type: String,
    default: undefined,
  },
  topLine: {
    type: Boolean,
    default: false,
  },
  bottomLine: {
    type: Boolean,
    default: false,
  },
  sections: {
    type: Array,
    default: undefined,
  },
});

const components = {
  Text: resolveComponent('LazyCtrText'),
  Head: resolveComponent('LazyCtrHead'),
  Files: resolveComponent('LazyCtrFiles'),
  TextComplex: resolveComponent('LazyCtrTextComplex'),
  CertificateImage: resolveComponent('LazyCtrCertificateImage'),
  AboutRequisites: resolveComponent('LazyCtrAboutRequisites'),
  Title: resolveComponent('LazyCtrTitle'),
  BanksSoftpos: resolveComponent('LazyCtrBanksSoftpos'),
  AboutLinks: resolveComponent('LazyCtrAboutLinks'),
  ContactsList: resolveComponent('LazyCtrContactsList'),
  ContactsSocials: resolveComponent('LazyCtrContactsSocials'),
  ListIconBlocks: resolveComponent('LazyCtrListIconBlocks'),
  ListChart: resolveComponent('LazyCtrListChart'),
  Questions: resolveComponent('LazyCtrQuestions'),
  ListAlphabet: resolveComponent('LazyCtrListAlphabet'),
  SecurityColumns: resolveComponent('LazyCtrSecurityColumns'),
  SecurityCards: resolveComponent('LazyCtrSecurityCards'),
  Image: resolveComponent('LazyCtrImage'),
  SecurityDigest: resolveComponent('LazyCtrSecurityDigest'),
  ListNumbered: resolveComponent('LazyCtrListNumbered'),
  FeedbackForm: resolveComponent('LazyCtrFeedbackForm'),
  KnowledgeStaff: resolveComponent('LazyCtrKnowledgeStaff'),
  Table: resolveComponent('LazyCtrTable'),
  PressInfo: resolveComponent('LazyCtrPressInfo'),
  CertificateMethods: resolveComponent('LazyCtrCertificateMethods'),
};

const sectionsFiltered = computed(() =>
  filterAdaptedSections(props.sections, components, 'CtrSidebar.vue'),
);
</script>

<template>
  <UiGridColumnsBase
    class="ctr-sidebar container"
    :class="{
      'ctr-sidebar--border-top': topLine,
      'ctr-sidebar--border-bottom': bottomLine,
      'ctr-sidebar--border-both': topLine && bottomLine,
    }"
  >
    <template v-if="title || icon" #1>
      <div class="ctr-sidebar__aside">
        <div class="ctr-sidebar__headline">
          <h2 v-if="title" class="ctr-sidebar__title">
            <span
              class="ctr-sidebar__font ctr-sidebar__font--title"
              v-html="$tp(title)"
            />
          </h2>

          <img v-if="icon" :src="icon" alt="icon" class="ctr-sidebar__img" />
        </div>

        <slot name="aside" />
      </div>
    </template>

    <template #2>
      <div class="ctr-sidebar__content">
        <slot name="content">
          <template v-if="sectionsFiltered">
            <Component
              :is="components[section.type]"
              v-for="(section, sectionIndex) in sectionsFiltered"
              :id="section.id"
              :key="sectionIndex"
              in-sidebar
              class="ctr-sidebar__section"
              v-bind="section.data"
            />
          </template>
        </slot>
      </div>
    </template>
  </UiGridColumnsBase>
</template>

<style scoped lang="scss">
.ctr-sidebar {
  $parent: &;

  position: relative;
  padding-top: em($sidebar-top-gap);

  @include media-breakpoint-down(sm) {
    padding-top: em(32);
    display: flex;
    flex-direction: column;
    gap: em(20);
  }

  &__headline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: em(20);

    @include media-breakpoint-down(sm) {
      align-items: center;
    }
  }

  &__img {
    flex-shrink: 0;
    margin-left: auto;
    width: em(156);
    height: em(156);
    object-fit: contain;
    object-position: center top;

    @include media-breakpoint-down(sm) {
      margin-left: 0;
      width: em(25);
      height: em(25);
    }
  }

  &__font {
    &--title {
      font-size: em(16);
      line-height: em(18, 16);
      text-transform: uppercase;

      @include media-breakpoint-down(sm) {
        line-height: em(21, 16);
      }
    }
  }

  &--border-top::before {
    content: '';
    position: absolute;
    top: 0;
    left: em($gap);
    right: em($gap);
    height: 1px;
    background: $color-gray-1;

    @include media-breakpoint-down(sm) {
      left: em($gap-sm);
      right: em($gap-sm);
    }
  }

  &--border-bottom {
    padding-bottom: em(97);

    @include media-breakpoint-down(sm) {
      padding-bottom: em(64);
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: em($gap);
      right: em($gap);
      height: 1px;
      background: $color-gray-1;

      @include media-breakpoint-down(sm) {
        left: em($gap-sm);
        right: em($gap-sm);
      }
    }
  }

  &--border-both::before {
    background: $color-red-1;
  }
}
</style>
